import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Footer from '../components/Footer'
import { getVisitors } from '../services/visitorApi'
import { checkInVisitor } from '../services/appointmentsApi'
import { useSocket } from '../hooks/useSocket'
import { loginUser, logout, getAuthUser, getAuthToken } from '../services/authService'
import { requestNotificationPermission, registerInstallation, onMessageReceived } from '../services/fcmService'
import { API_BASE_URL } from '../services/apiConfig'

export default function Security() {
  const navigate = useNavigate()
  const storedUser = getAuthUser()
  const isSecurityUser = !!getAuthToken() && storedUser?.role === 'security'
  const [isLoggedIn, setIsLoggedIn] = useState(isSecurityUser)
  const [currentUser, setCurrentUser] = useState(isSecurityUser ? storedUser : null)
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [visitors, setVisitors] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedVisitor, setSelectedVisitor] = useState(null)
  const [actionLoading, setActionLoading] = useState(null)
  const { socket, isConnected } = useSocket()

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoginLoading(true)
    setError('')

    try {
      const result = await loginUser(formData.email, formData.password, 'security')
      setIsLoggedIn(true)
      setCurrentUser(result.user)
      setFormData({ email: '', password: '' })
      
      loadVisitors()
    } catch (err) {
      setError(err.message || 'Invalid credentials')
    } finally {
      setLoginLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    setIsLoggedIn(false)
    setCurrentUser(null)
    setFormData({ email: '', password: '' })
    setVisitors([])
    setSelectedVisitor(null)
    navigate('/')
  }

  const loadVisitors = async () => {
    try {
      setLoading(true)
      const data = await getVisitors()
      setVisitors(data)
    } catch (err) {
      console.error('Error loading visitors:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    loadVisitors()
  }

  const handleCheckIn = async (visitorId) => {
    try {
      setActionLoading(visitorId)
      await checkInVisitor(visitorId)
      loadVisitors()
      setSelectedVisitor(null)
    } catch (err) {
      console.error('Error checking in visitor:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const handleCheckOut = async (visitorId) => {
    try {
      setActionLoading(visitorId)
      const response = await fetch(`${API_BASE_URL}/appointments/${visitorId}/check-out`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!response.ok) throw new Error('Failed to check out')
      loadVisitors()
      setSelectedVisitor(null)
    } catch (err) {
      console.error('Error checking out visitor:', err)
    } finally {
      setActionLoading(null)
    }
  }

  useEffect(() => {
    if (!isLoggedIn || !currentUser?.id) return

    const setupNotifications = async () => {
      try {
        const hasPermission = await requestNotificationPermission()
        if (hasPermission) {
          await registerInstallation(currentUser.id, 'security')
        }
      } catch (error) {
        console.log('Notification setup skipped (optional)')
      }
    }

    setupNotifications()

    // Refresh when a push arrives (it is shown in the notification panel by the service worker)
    return onMessageReceived((payload) => {
      if (payload.data?.url !== '/security') return

      getVisitors().then(setVisitors).catch((error) => {
        console.error('Error loading visitors after notification:', error)
      })
    })
  }, [isLoggedIn, currentUser?.id])

  useEffect(() => {
    if (!socket || !isConnected) return

    socket.emit('join-security-room')

    socket.on('visitor-registered', () => {
      loadVisitors()
    })
    socket.on('appointment-accepted', () => {
      loadVisitors()
    })
    socket.on('appointment-rejected', () => {
      loadVisitors()
    })

    return () => {
      socket.off('visitor-registered')
      socket.off('appointment-accepted')
      socket.off('appointment-rejected')
    }
  }, [socket, isConnected])

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-white to-saffron-50">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <button
              onClick={() => navigate('/')}
              className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </button>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="text-center mb-8">
                <img src="/logo.png" alt="igus Logo" className="mx-auto mb-4 h-16 w-auto" />
                <h1 className="text-2xl font-bold text-gray-900">Security Login</h1>
                <p className="mt-2 text-sm text-gray-600">Enter your credentials to access the security panel</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
                    Security Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="security@igus.net"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter your password"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                    required
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full rounded-lg bg-saffron-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-saffron-700 focus:outline-none focus:ring-2 focus:ring-saffron-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loginLoading ? 'Logging in...' : 'Login'}
                </button>
              </form>

              <div className="mt-6 border-t border-gray-200 pt-6">
                <p className="text-xs text-gray-500 text-center">
                  <span className="block mb-2">Contact your administrator</span>
                  <span className="block">if you need assistance with login credentials</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-white to-saffron-50">
      <header className="bg-white shadow-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="igus Logo" className="h-10 w-auto" />
            <h1 className="text-xl font-bold text-gray-900">Security Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Security Dashboard</h2>
          <p className="mt-1 text-gray-600">Monitor and manage visitor access and security</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active Visitors</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {visitors.filter(v => v.status === 'checked_in' || v.status === 'accepted').length}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Checked Out</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {visitors.filter(v => v.status === 'checked_out').length}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending Check-in</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {visitors.filter(v => v.status === 'pending' || v.status === 'accepted').length}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Visitors</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{visitors.length}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3.598a4 4 0 01-3.996-3.558m0 0l.674-3.372m.674 3.372a4 4 0 004.323 4.102M9.867 9a4 4 0 110-5.292m0 5.292a4.389 4.389 0 011.946 1.946" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Visitors List */}
        <div className="mt-8 grid gap-6">
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Visitor Status</h3>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading visitors...</div>
            ) : visitors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No visitors registered yet</div>
            ) : (
              <div className="space-y-2">
                {visitors.map((visitor) => (
                  <div key={visitor.id} className="flex items-center justify-between rounded-lg bg-gray-50 p-4 hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{visitor.full_name}</p>
                      <p className="text-sm text-gray-500">{visitor.organization} • {visitor.purpose}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Appointment: {visitor.appointment_date} at {visitor.appointment_time}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                        visitor.status === 'checked_in' ? 'bg-green-100 text-green-800' :
                        visitor.status === 'checked_out' ? 'bg-gray-100 text-gray-800' :
                        visitor.status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {visitor.status.replace(/_/g, ' ')}
                      </span>
                      <button
                        onClick={() => setSelectedVisitor(visitor)}
                        className="rounded-lg bg-saffron-600 px-3 py-2 text-xs font-medium text-white hover:bg-saffron-700"
                      >
                        View Details
                      </button>
                      {visitor.status === 'pending' || visitor.status === 'accepted' ? (
                        <button
                          onClick={() => handleCheckIn(visitor.id)}
                          disabled={actionLoading === visitor.id}
                          className="rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          {actionLoading === visitor.id ? 'Checking In...' : 'Check In'}
                        </button>
                      ) : visitor.status === 'checked_in' ? (
                        <button
                          onClick={() => handleCheckOut(visitor.id)}
                          disabled={actionLoading === visitor.id}
                          className="rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          {actionLoading === visitor.id ? 'Checking Out...' : 'Check Out'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Visitor Details Modal */}
      {selectedVisitor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Visitor Details</h2>
              <button
                onClick={() => setSelectedVisitor(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Full Name</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.full_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Gender</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.gender}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Phone</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.phone}</p>
                  </div>
                </div>
              </div>

              {/* Visit Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Visit Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Organization</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.organization}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Purpose</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.purpose}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Appointment Date</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.appointment_date}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Appointment Time</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.appointment_time}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm font-medium text-gray-500">Reason</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.reason}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Employee Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Employee Name</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.employee_details?.name || 'Not available'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Employee ID</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.employee_details?.employee_id || 'Not available'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Department</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.employee_details?.department || 'Not available'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Designation / Role</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.employee_details?.designation || 'Not available'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Official igus Email</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.employee_details?.email || 'Not available'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Official Contact Number / Extension</p>
                    <p className="mt-1 text-gray-900">{selectedVisitor.employee_details?.contact || 'Not available'}</p>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              {selectedVisitor.additional_notes && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Notes</h3>
                  <p className="text-gray-900">{selectedVisitor.additional_notes}</p>
                </div>
              )}

              {/* Signature */}
              {selectedVisitor.signature && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Signature</h3>
                  <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                    <img
                      src={selectedVisitor.signature}
                      alt="Visitor Signature"
                      className="max-h-32 mx-auto"
                    />
                  </div>
                </div>
              )}

              {/* Check-in/Check-out Times */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Check-in/Check-out Times</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Check-in Time</p>
                    <p className="mt-1 text-gray-900">
                      {selectedVisitor.check_in_time ? new Date(selectedVisitor.check_in_time).toLocaleString() : 'Not checked in'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Check-out Time</p>
                    <p className="mt-1 text-gray-900">
                      {selectedVisitor.check_out_time ? new Date(selectedVisitor.check_out_time).toLocaleString() : 'Not checked out'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
                <span className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-medium ${
                  selectedVisitor.status === 'checked_in' ? 'bg-green-100 text-green-800' :
                  selectedVisitor.status === 'checked_out' ? 'bg-gray-100 text-gray-800' :
                  selectedVisitor.status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {selectedVisitor.status.replace(/_/g, ' ')}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {selectedVisitor.status === 'pending' || selectedVisitor.status === 'accepted' ? (
                  <button
                    onClick={() => handleCheckIn(selectedVisitor.id)}
                    disabled={actionLoading === selectedVisitor.id}
                    className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {actionLoading === selectedVisitor.id ? 'Checking In...' : 'Check In Visitor'}
                  </button>
                ) : selectedVisitor.status === 'checked_in' ? (
                  <button
                    onClick={() => handleCheckOut(selectedVisitor.id)}
                    disabled={actionLoading === selectedVisitor.id}
                    className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {actionLoading === selectedVisitor.id ? 'Checking Out...' : 'Check Out Visitor'}
                  </button>
                ) : null}
                <button
                  onClick={() => setSelectedVisitor(null)}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  )
}
